import { Button, Divider, Flex, Table } from 'antd'
import { ColumnType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useInvestigationsServiceGetInvestigationTestsEndpoint } from 'src/api/queries'
import {
  InvestigationTestsListItemDto,
  InvestigationTestStatus
} from 'src/api/requests'
import { PageBreadcrumbs, SearchBar, Select, TbTag } from 'src/components'
import { getFullName } from 'src/features/users/utils'
import { useTablePagination } from 'src/hooks'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { resetPagination } from 'src/utils'

import { TestStatusTag } from '../components'

export const TestsResultsPage = () => {
  const { t, currentLang } = useLocales()
  const navigate = useNavigate()

  const pagination = useTablePagination()

  const [search, setSearch] = useState<string>()
  const [status, setStatus] = useState<string>()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!status && {
      status: status as InvestigationTestStatus
    })
  }

  const { data, isLoading } =
    useInvestigationsServiceGetInvestigationTestsEndpoint(queryParams)

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onStatusChange = (val: string) => {
    setStatus(val)
    resetPagination(pagination)
  }

  const columns: ColumnType<InvestigationTestsListItemDto>[] = [
    {
      key: 'code',
      fixed: 'left',
      title: t('investigations.test_number'),
      render: (_, { code, isTb }) => (
        <Flex gap={8} align="center">
          {code}
          <TbTag isTb={isTb} />
        </Flex>
      )
    },
    {
      key: 'creation date',
      title: t('consultations.harvest_date'),
      render: (_, { testingAt }) =>
        testingAt
          ? dayjs
              .utc(testingAt)
              .tz()
              .format(dayMonthYearHourMinutesFormatWithDot)
          : '-'
    },
    {
      key: 'type',
      title: t('tests_results.type'),
      render: (_, { category }) => category?.translation?.[currentLang.value]
    },
    {
      key: 'status',
      title: t('form.status'),
      render: (_, { status }) => <TestStatusTag status={status} />
    },
    {
      key: 'referral doctor',
      title: t('investigations.referral_doctor'),
      render: (_, { referralDoctorName, referralDoctorUser }) =>
        referralDoctorName ||
        getFullName(
          referralDoctorUser?.firstName,
          referralDoctorUser?.lastName
        ) ||
        '-'
    },
    {
      key: 'details',
      fixed: 'right',
      render: ({ id, investigationId }) => (
        <Button
          type="link"
          onClick={() => navigate(paths.tests.testDetails(investigationId, id))}
        >
          {t('common.details')}
        </Button>
      )
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('menu.tests_results')}
      items={[{ title: t('menu.tests_results') }]}
    >
      <Flex justify="space-between" align="center">
        <SearchBar maxWidth={320} onSearch={onSearch} />

        <Select
          allowClear
          placeholder={t('form.status')}
          options={Object.values(InvestigationTestStatus).map((status) => ({
            label: t(`tests_results.statuses.${status}`),
            value: status
          }))}
          onChange={onStatusChange}
        />
      </Flex>

      <Divider />

      <Table
        rowKey="id"
        scroll={{ x: 'max-content' }}
        loading={isLoading}
        columns={columns}
        dataSource={data?.data}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </PageBreadcrumbs>
  )
}
